import { useState, useEffect } from 'react'
import {
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  Trash2,
  RefreshCw,
  FileText,
  Building2,
  MapPin,
  Sparkles,
  Eye,
  Edit3,
  RotateCcw,
  Pencil,
  CheckSquare,
  Square,
  Loader2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Printer,
  Download,
  Check,
  Globe,
  Filter,
} from 'lucide-react'
import api, { API_BASE_URL } from '@/services/api'
import toast from 'react-hot-toast'
import ColdLetterEditor from '@/components/ColdLetterEditor'
import { useAuthStore } from '@/store/authStore'
import type { ColdLetter, ColdLetterStats, Client, AvailableColdContact } from '@/types'

type TabType = 'pending' | 'approved' | 'rejected' | 'deleted' | 'all'

// Store client data by client ID
const clientDataCache: Record<number, {
  logo: string | null
  portfolioImages: (string | null)[]
  websiteUrl: string | null
}> = {}

export default function ColdLetters() {
  const [activeTab, setActiveTab] = useState<TabType>('pending')
  const [letters, setLetters] = useState<ColdLetter[]>([])
  const [stats, setStats] = useState<ColdLetterStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedLetter, setSelectedLetter] = useState<ColdLetter | null>(null)
  const [editingLetter, setEditingLetter] = useState<ColdLetter | null>(null)
  const [clients, setClients] = useState<Client[]>([])
  const [contacts, setContacts] = useState<AvailableColdContact[]>([])
  const [generating, setGenerating] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [selectedContactIds, setSelectedContactIds] = useState<number[]>([])
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null)
  const [selectedClientLogo, setSelectedClientLogo] = useState<string | null>(null)
  const [selectedClientPortfolio, setSelectedClientPortfolio] = useState<(string | null)[]>([null, null, null])
  const [selectedClientWebsite, setSelectedClientWebsite] = useState<string | null>(null)
  const [generationProgress, setGenerationProgress] = useState<{
    current: number
    total: number
    phase: string
    message: string
    successCount: number
    failedCount: number
    skippedCount: number
  } | null>(null)
  const [previewZoom, setPreviewZoom] = useState<number>(150) // 150% default
  const [selectedForPrint, setSelectedForPrint] = useState<number[]>([])
  const [printingBatch, setPrintingBatch] = useState(false)
  const [printingSingle, setPrintingSingle] = useState(false)

  // "All Letters" tab filters — only applied when activeTab === 'all'
  const [filterClientId, setFilterClientId] = useState<number | null>(null)
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [filterCreatedAfter, setFilterCreatedAfter] = useState<string>('')
  const [filterCreatedBefore, setFilterCreatedBefore] = useState<string>('')

  useEffect(() => {
    fetchStats()
    fetchClients()
  }, [])

  useEffect(() => {
    fetchLetters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filterClientId, filterStatus, filterCreatedAfter, filterCreatedBefore])

  // Fetch client data when letter is selected
  useEffect(() => {
    if (selectedLetter) {
      const clientId = selectedLetter.clientId
      if (clientDataCache[clientId] !== undefined) {
        setSelectedClientLogo(clientDataCache[clientId].logo)
        setSelectedClientPortfolio(clientDataCache[clientId].portfolioImages)
        setSelectedClientWebsite(clientDataCache[clientId].websiteUrl)
      } else {
        api.get(`/api/v1/clients/${clientId}`)
          .then(res => {
            const data = {
              logo: res.data.logoPath || null,
              portfolioImages: [
                res.data.portfolioImage1 || null,
                res.data.portfolioImage2 || null,
                res.data.portfolioImage3 || null,
              ],
              websiteUrl: res.data.websiteUrl || null,
            }
            clientDataCache[clientId] = data
            setSelectedClientLogo(data.logo)
            setSelectedClientPortfolio(data.portfolioImages)
            setSelectedClientWebsite(data.websiteUrl)
          })
          .catch(() => {
            const data = { logo: null, portfolioImages: [null, null, null], websiteUrl: null }
            clientDataCache[clientId] = data
            setSelectedClientLogo(null)
            setSelectedClientPortfolio([null, null, null])
            setSelectedClientWebsite(null)
          })
      }
    }
  }, [selectedLetter])

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/v1/cold-letters/stats')
      setStats(response.data)
    } catch {
      // Stats fetch failed
    }
  }

  const fetchLetters = async () => {
    setLoading(true)
    try {
      let endpoint = '/api/v1/cold-letters/'
      if (activeTab === 'pending') endpoint = '/api/v1/cold-letters/pending'
      else if (activeTab === 'approved') endpoint = '/api/v1/cold-letters/approved'
      else if (activeTab === 'deleted') endpoint = '/api/v1/cold-letters/deleted'
      else if (activeTab === 'rejected') endpoint = '/api/v1/cold-letters/?status=rejected'
      else if (activeTab === 'all') {
        // Apply the filter bar's selections as query params
        const params = new URLSearchParams()
        if (filterClientId != null) params.set('client_id', String(filterClientId))
        if (filterStatus) params.set('status', filterStatus)
        if (filterCreatedAfter) params.set('created_after', filterCreatedAfter)
        if (filterCreatedBefore) params.set('created_before', filterCreatedBefore)
        const qs = params.toString()
        endpoint = qs ? `/api/v1/cold-letters/?${qs}` : '/api/v1/cold-letters/'
      }

      const response = await api.get(endpoint)
      setLetters(response.data)
    } catch {
      toast.error('Failed to load cold letters')
    } finally {
      setLoading(false)
    }
  }

  const hasActiveFilters =
    activeTab === 'all' &&
    (filterClientId != null || filterStatus !== '' || filterCreatedAfter !== '' || filterCreatedBefore !== '')

  const clearFilters = () => {
    setFilterClientId(null)
    setFilterStatus('')
    setFilterCreatedAfter('')
    setFilterCreatedBefore('')
  }

  const fetchClients = async () => {
    try {
      const response = await api.get('/api/v1/clients/')
      setClients(response.data)
    } catch {
      // Clients fetch failed
    }
  }

  const fetchContacts = async (clientId: number) => {
    try {
      const response = await api.get(`/api/v1/cold-letters/available-contacts/${clientId}`)
      setContacts(response.data)
    } catch {
      setContacts([])
    }
  }

  const selectNextLetter = (currentId: number) => {
    const currentIndex = letters.findIndex(l => l.id === currentId)
    if (currentIndex >= 0 && letters.length > 1) {
      const nextIndex = currentIndex < letters.length - 1 ? currentIndex + 1 : currentIndex - 1
      setSelectedLetter(letters[nextIndex])
    } else {
      setSelectedLetter(null)
    }
  }

  const handleApprove = async (letterId: number) => {
    try {
      await api.post(`/api/v1/cold-letters/${letterId}/approve`)
      toast.success('Letter approved')
      selectNextLetter(letterId)
      fetchLetters()
      fetchStats()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to approve letter')
    }
  }

  const handleReject = async (letterId: number) => {
    try {
      await api.post(`/api/v1/cold-letters/${letterId}/reject`, { reason: 'Rejected' })
      toast.success('Letter rejected')
      selectNextLetter(letterId)
      fetchLetters()
      fetchStats()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reject letter')
    }
  }

  const handleDelete = async (letterId: number) => {
    try {
      await api.delete(`/api/v1/cold-letters/${letterId}`, {
        data: { reason: 'Deleted', deletion_reason: 'other' }
      })
      toast.success('Letter deleted')
      fetchLetters()
      fetchStats()
      setSelectedLetter(null)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete letter')
    }
  }

  const handleRestore = async (letterId: number) => {
    try {
      await api.post(`/api/v1/cold-letters/${letterId}/restore`)
      toast.success('Letter restored')
      fetchLetters()
      fetchStats()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to restore letter')
    }
  }

  const handleRegenerate = async (letterId: number) => {
    setRegenerating(true)
    try {
      const response = await api.post(`/api/v1/cold-letters/${letterId}/regenerate`, {})
      toast.success('Letter regenerated')
      fetchLetters()
      setSelectedLetter(response.data)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate letter')
    } finally {
      setRegenerating(false)
    }
  }

  // Print functions
  const handlePrintSingle = async (letterId: number) => {
    setPrintingSingle(true)
    try {
      const response = await api.get(`/api/v1/cold-letters/${letterId}/pdf?download=false&save=true`, {
        responseType: 'blob',
      })

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const printWindow = window.open(url, '_blank')

      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
        }
      }

      toast.success('PDF ready for printing')
    } catch (error: any) {
      toast.error('Failed to generate PDF')
    } finally {
      setPrintingSingle(false)
    }
  }

  const handlePrintBatch = async (markAsPrinted: boolean = false) => {
    if (selectedForPrint.length === 0) {
      toast.error('No letters selected for printing')
      return
    }

    setPrintingBatch(true)
    try {
      const response = await api.post('/api/v1/cold-letters/batch-pdf', {
        letter_ids: selectedForPrint,
        mark_as_printed: markAsPrinted,
        save_individual: true,
      }, {
        responseType: 'blob',
      })

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const printWindow = window.open(url, '_blank')

      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
        }
      }

      toast.success(`${selectedForPrint.length} letters ready for printing`)

      if (markAsPrinted) {
        setSelectedForPrint([])
        fetchLetters()
        fetchStats()
      }
    } catch (error: any) {
      toast.error('Failed to generate batch PDF')
    } finally {
      setPrintingBatch(false)
    }
  }

  const handlePrintAllApproved = async (markAsPrinted: boolean = false) => {
    const approvedLetters = letters.filter(l => l.status === 'approved')
    if (approvedLetters.length === 0) {
      toast.error('No approved letters to print')
      return
    }

    setSelectedForPrint(approvedLetters.map(l => l.id))

    setPrintingBatch(true)
    try {
      const response = await api.post('/api/v1/cold-letters/batch-pdf', {
        letter_ids: approvedLetters.map(l => l.id),
        mark_as_printed: markAsPrinted,
        save_individual: true,
      }, {
        responseType: 'blob',
      })

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const printWindow = window.open(url, '_blank')

      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
        }
      }

      toast.success(`${approvedLetters.length} letters ready for printing`)

      if (markAsPrinted) {
        setSelectedForPrint([])
        fetchLetters()
        fetchStats()
      }
    } catch (error: any) {
      toast.error('Failed to generate batch PDF')
    } finally {
      setPrintingBatch(false)
    }
  }

  const toggleSelectForPrint = (letterId: number) => {
    setSelectedForPrint(prev =>
      prev.includes(letterId)
        ? prev.filter(id => id !== letterId)
        : [...prev, letterId]
    )
  }

  const toggleSelectAllForPrint = () => {
    const approvedLetters = letters.filter(l => l.status === 'approved')
    if (selectedForPrint.length === approvedLetters.length) {
      setSelectedForPrint([])
    } else {
      setSelectedForPrint(approvedLetters.map(l => l.id))
    }
  }

  const handleGenerate = async () => {
    if (selectedContactIds.length === 0 || !selectedClientId) {
      toast.error('Please select at least one cold business and a client')
      return
    }

    setGenerating(true)
    setGenerationProgress({
      current: 0,
      total: selectedContactIds.length,
      phase: 'starting',
      message: 'Starting batch generation...',
      successCount: 0,
      failedCount: 0,
      skippedCount: 0,
    })

    try {
      const token = useAuthStore.getState().accessToken

      const response = await fetch(`${API_BASE_URL}/api/v1/cold-letters/generate-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          cold_contact_ids: selectedContactIds,
          client_id: selectedClientId,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to start generation')
      }

      const reader = response.body?.getReader()
      const decoder = new TextDecoder()

      if (!reader) {
        throw new Error('No response stream')
      }

      let successCount = 0
      let failedCount = 0
      let skippedCount = 0

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const text = decoder.decode(value)
        const lines = text.split('\n')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))

              if (data.type === 'progress') {
                setGenerationProgress({
                  current: data.current,
                  total: data.total,
                  phase: data.phase,
                  message: data.message,
                  successCount,
                  failedCount,
                  skippedCount,
                })
              } else if (data.type === 'item_complete') {
                successCount++
                setGenerationProgress(prev => prev ? {
                  ...prev,
                  current: data.current,
                  message: data.message,
                  successCount,
                } : null)
              } else if (data.type === 'item_error') {
                failedCount++
                setGenerationProgress(prev => prev ? {
                  ...prev,
                  current: data.current,
                  message: data.message,
                  failedCount,
                } : null)
              } else if (data.type === 'item_skipped') {
                skippedCount++
                setGenerationProgress(prev => prev ? {
                  ...prev,
                  current: data.current,
                  message: data.message,
                  skippedCount,
                } : null)
              } else if (data.type === 'complete') {
                if (data.success > 0) {
                  toast.success(`Generated ${data.success} letter(s)`)
                }
                if (data.skipped > 0) {
                  toast.success(`Skipped ${data.skipped} (already exist)`, { icon: '⏭️' })
                }
                if (data.failed > 0) {
                  toast.error(`Failed to generate ${data.failed} letter(s)`)
                }
              } else if (data.type === 'error') {
                toast.error(data.message)
              }
            } catch (e) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }

      setShowGenerateModal(false)
      setSelectedContactIds([])
      fetchLetters()
      fetchStats()
      if (selectedClientId) {
        fetchContacts(selectedClientId)
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to generate letters')
    } finally {
      setGenerating(false)
      setGenerationProgress(null)
    }
  }

  const toggleContactSelection = (contactId: number) => {
    setSelectedContactIds(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    )
  }

  const selectAllContacts = () => {
    if (selectedContactIds.length === contacts.length) {
      setSelectedContactIds([])
    } else {
      setSelectedContactIds(contacts.map(c => c.id))
    }
  }

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      approved: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
      printed: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      mailed: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      deleted: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    }

    const icons: Record<string, React.ReactNode> = {
      pending: <Clock className="w-3 h-3" />,
      in_progress: <Edit3 className="w-3 h-3" />,
      approved: <CheckCircle className="w-3 h-3" />,
      rejected: <XCircle className="w-3 h-3" />,
      deleted: <Trash2 className="w-3 h-3" />,
    }

    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full border ${styles[status] || styles.pending}`}>
        {icons[status]}
        {status.replace('_', ' ').charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>
    )
  }

  const tabs = [
    { id: 'pending' as TabType, label: 'Pending Review', count: stats?.pending || 0, icon: Clock },
    { id: 'approved' as TabType, label: 'Approved', count: stats?.approved || 0, icon: CheckCircle },
    { id: 'rejected' as TabType, label: 'Rejected', count: stats?.rejected || 0, icon: XCircle },
    { id: 'deleted' as TabType, label: 'Deleted', count: stats?.deleted || 0, icon: Trash2 },
    { id: 'all' as TabType, label: 'All Letters', count: stats?.total || 0, icon: Mail },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Cold Letters</h1>
          <p className="text-slate-400 mt-1">Generate and manage cold outreach letters</p>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'approved' && letters.length > 0 && (
            <>
              <button
                onClick={() => handlePrintBatch(false)}
                disabled={printingBatch || selectedForPrint.length === 0}
                className="btn btn-secondary flex items-center gap-2 disabled:opacity-50"
              >
                {printingBatch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Export Selected ({selectedForPrint.length})
              </button>
              <button
                onClick={() => handlePrintAllApproved(false)}
                disabled={printingBatch}
                className="btn btn-secondary flex items-center gap-2 disabled:opacity-50"
              >
                {printingBatch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                Export All ({letters.length})
              </button>
              <button
                onClick={() => handlePrintAllApproved(true)}
                disabled={printingBatch}
                className="btn bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2 disabled:opacity-50"
                title="Export all and mark as printed"
              >
                {printingBatch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Print & Complete
              </button>
            </>
          )}
          <button
            onClick={() => {
              setShowGenerateModal(true)
              if (clients.length > 0) {
                setSelectedClientId(clients[0].id)
                fetchContacts(clients[0].id)
              }
            }}
            className="btn-primary flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            Generate Letter
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-100">{stats?.pending || 0}</p>
              <p className="text-sm text-slate-400">Pending</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-100">{stats?.approved || 0}</p>
              <p className="text-sm text-slate-400">Approved</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-100">{stats?.rejected || 0}</p>
              <p className="text-sm text-slate-400">Rejected</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Mail className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-100">{stats?.total || 0}</p>
              <p className="text-sm text-slate-400">Total</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-700/50 pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-[#f39c13] text-white'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            <span className={`px-1.5 py-0.5 text-xs rounded-full ${
              activeTab === tab.id ? 'bg-white/20' : 'bg-slate-700'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Filter bar — only on the "All Letters" tab */}
      {activeTab === 'all' && (
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-400" />
            <h3 className="text-sm font-medium text-slate-300">Filter letters</h3>
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Client</label>
              <select
                className="input w-full"
                value={filterClientId ?? ''}
                onChange={(e) => setFilterClientId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">All clients</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.companyName}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Status</label>
              <select
                className="input w-full"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="printed">Printed</option>
                <option value="mailed">Mailed</option>
                <option value="deleted">Deleted</option>
              </select>
            </div>
            <div className="flex-1 min-w-[150px]">
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Created from</label>
              <input
                type="date"
                className="input w-full"
                value={filterCreatedAfter}
                onChange={(e) => setFilterCreatedAfter(e.target.value)}
                max={filterCreatedBefore || undefined}
              />
            </div>
            <div className="flex-1 min-w-[150px]">
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Created to</label>
              <input
                type="date"
                className="input w-full"
                value={filterCreatedBefore}
                onChange={(e) => setFilterCreatedBefore(e.target.value)}
                min={filterCreatedAfter || undefined}
              />
            </div>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="btn-secondary text-sm">
                Clear filters
              </button>
            )}
          </div>
          {hasActiveFilters && (
            <p className="mt-3 text-xs text-slate-500">
              Showing {letters.length} matching letter{letters.length === 1 ? '' : 's'}
            </p>
          )}
        </div>
      )}

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 flex-1" style={{ minHeight: 'calc(100vh - 280px)' }}>
        {/* Letter List */}
        <div className="card overflow-hidden lg:col-span-2 flex flex-col" style={{ maxHeight: 'calc(100vh - 220px)' }}>
          <div className="px-4 py-3 bg-slate-800/30 border-b border-slate-700/50">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-slate-200">
                {tabs.find(t => t.id === activeTab)?.label}
              </h3>
              {activeTab === 'approved' && letters.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleSelectAllForPrint}
                    className="p-1.5 rounded hover:bg-slate-700/50 text-slate-400 hover:text-slate-200"
                    title={selectedForPrint.length === letters.length ? 'Deselect all' : 'Select all'}
                  >
                    {selectedForPrint.length === letters.length ? (
                      <CheckSquare className="w-4 h-4" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>
                  {selectedForPrint.length > 0 && (
                    <span className="text-xs text-slate-400">{selectedForPrint.length} selected</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 text-slate-500 animate-spin mx-auto mb-2" />
              <p className="text-slate-400">Loading letters...</p>
            </div>
          ) : letters.length === 0 ? (
            <div className="p-8 text-center">
              <Mail className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-200 mb-2">No letters found</h3>
              <p className="text-slate-400">
                {activeTab === 'pending'
                  ? 'Generate a letter to get started'
                  : 'No letters in this category'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-700/30 overflow-y-auto flex-1 scrollbar-thin">
              {letters.map((letter) => (
                <div
                  key={letter.id}
                  onClick={() => setSelectedLetter(letter)}
                  onDoubleClick={() => letter.status !== 'deleted' && setEditingLetter(letter)}
                  className={`p-4 cursor-pointer hover:bg-slate-800/30 transition-colors ${
                    selectedLetter?.id === letter.id ? 'bg-slate-800/50' : ''
                  }`}
                  title="Double-click to edit"
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox for approved letters */}
                    {activeTab === 'approved' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleSelectForPrint(letter.id)
                        }}
                        className="mt-0.5 p-0.5 rounded hover:bg-slate-700/50"
                      >
                        {selectedForPrint.includes(letter.id) ? (
                          <CheckSquare className="w-4 h-4 text-emerald-400" />
                        ) : (
                          <Square className="w-4 h-4 text-slate-500" />
                        )}
                      </button>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className="w-4 h-4 text-slate-500" />
                        <span className="font-medium text-slate-200 truncate">
                          {letter.clientName || 'Unknown Client'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <FileText className="w-3 h-3" />
                        <span className="truncate">{letter.contactBusinessName || 'Unknown Business'}</span>
                      </div>
                      {letter.territoryName && (
                        <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                          <MapPin className="w-3 h-3" />
                          <span>{letter.territoryName}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {getStatusBadge(letter.status)}
                      <span className="text-xs text-slate-500">
                        {new Date(letter.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Letter Preview */}
        <div className="card overflow-hidden lg:col-span-3 flex flex-col">
          <div className="px-4 py-3 bg-slate-800/30 border-b border-slate-700/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="font-medium text-slate-200">Letter Preview</h3>
              {selectedLetter && (
                <div className="flex items-center gap-1 bg-slate-700/50 rounded-lg p-0.5">
                  <button
                    onClick={() => setPreviewZoom(Math.max(50, previewZoom - 25))}
                    className="p-1 rounded hover:bg-slate-600 text-slate-400 hover:text-slate-200"
                    title="Zoom out"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <span className="text-xs text-slate-400 w-10 text-center">{previewZoom}%</span>
                  <button
                    onClick={() => setPreviewZoom(Math.min(150, previewZoom + 25))}
                    className="p-1 rounded hover:bg-slate-600 text-slate-400 hover:text-slate-200"
                    title="Zoom in"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPreviewZoom(100)}
                    className="p-1 rounded hover:bg-slate-600 text-slate-400 hover:text-slate-200"
                    title="Reset zoom"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            {selectedLetter && (
              <div className="flex items-center gap-2">
                {selectedLetter.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleApprove(selectedLetter.id)}
                      className="p-1.5 rounded hover:bg-emerald-500/20 text-emerald-400"
                      title="Approve"
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleReject(selectedLetter.id)}
                      className="p-1.5 rounded hover:bg-red-500/20 text-red-400"
                      title="Reject"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </>
                )}
                {selectedLetter.status !== 'deleted' && (
                  <>
                    <button
                      onClick={() => handlePrintSingle(selectedLetter.id)}
                      disabled={printingSingle}
                      className="p-1.5 rounded hover:bg-purple-500/20 text-purple-400 disabled:opacity-50"
                      title="Download PDF"
                    >
                      {printingSingle ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => setEditingLetter(selectedLetter)}
                      className="p-1.5 rounded hover:bg-[#f39c13]/20 text-[#f39c13]"
                      title="Edit letter"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleRegenerate(selectedLetter.id)}
                      className="p-1.5 rounded hover:bg-blue-500/20 text-blue-400"
                      title="Regenerate"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(selectedLetter.id)}
                      className="p-1.5 rounded hover:bg-slate-500/20 text-slate-400"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
                {selectedLetter.status === 'deleted' && (
                  <button
                    onClick={() => handleRestore(selectedLetter.id)}
                    className="p-1.5 rounded hover:bg-amber-500/20 text-amber-400"
                    title="Restore"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>

          {selectedLetter ? (
            <div className="p-4 relative">
              {/* Regeneration Overlay */}
              {regenerating && (
                <div className="absolute inset-0 bg-slate-800/90 z-10 flex flex-col items-center justify-center rounded-lg">
                  <RefreshCw className="w-10 h-10 text-blue-500 animate-spin mb-3" />
                  <p className="text-slate-200 font-medium">Regenerating...</p>
                  <div className="mt-3 w-48 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{ width: '100%' }} />
                  </div>
                </div>
              )}
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-slate-200">{selectedLetter.clientName}</h4>
                  <p className="text-sm text-slate-400">{selectedLetter.contactBusinessName}</p>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusBadge(selectedLetter.status)}
                </div>
              </div>

              <div className="overflow-auto flex-1 p-4 bg-slate-600/20" style={{ maxHeight: 'calc(100vh - 400px)' }}>
                {/* Wrapper to allow scrolling of scaled content */}
                <div style={{
                  width: `${595 * previewZoom / 100}px`,
                  height: `${842 * previewZoom / 100}px`,
                  margin: '0 auto',
                  position: 'relative'
                }}>
                  {/* A4 paper - fixed size 595x842px, scaled by zoom */}
                  <div
                    className="bg-white rounded shadow-lg absolute top-0 left-0"
                    style={{
                      width: '595px',
                      height: '842px',
                      transform: `scale(${previewZoom / 100})`,
                      transformOrigin: 'top left',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Logo header */}
                    <div className="pt-10 px-12 pb-2" style={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
                      {selectedClientLogo && (
                        <div className="flex justify-end">
                          <img
                            src={selectedClientLogo.startsWith('/') ? selectedClientLogo : `/${selectedClientLogo}`}
                            alt={selectedLetter.clientName || 'Logo'}
                            className="max-h-16 max-w-[160px] object-contain"
                          />
                        </div>
                      )}
                    </div>
                    {/* Letter content */}
                    <div
                      className="px-12 text-slate-900 letter-preview absolute"
                      style={{
                        fontFamily: "'Aptos', 'Calibri', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
                        fontSize: '10px',
                        lineHeight: '1.4',
                        top: selectedClientLogo ? '70px' : '35px',
                        left: 0,
                        right: 0
                      }}
                      dangerouslySetInnerHTML={{
                        __html: selectedLetter.content.includes('<p>')
                          ? selectedLetter.content
                          : selectedLetter.content.replace(/\n/g, '<br>')
                      }}
                    />

                    {/* Portfolio Images & Footer - positioned at bottom */}
                    {(selectedClientPortfolio.some(img => img) || selectedClientWebsite) && (
                      <div className="absolute bottom-6 left-12 right-12">
                        {/* Portfolio Images */}
                        {selectedClientPortfolio.some(img => img) && (
                          <div className="mb-2">
                            <div className="border-t border-gray-300 mb-2"></div>
                            <div className="flex gap-2 justify-center">
                              {selectedClientPortfolio.map((img, idx) => (
                                img ? (
                                  <img
                                    key={idx}
                                    src={img.startsWith('/') ? img : `/${img}`}
                                    alt={`Portfolio ${idx + 1}`}
                                    className="h-12 w-auto object-cover rounded"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none'
                                    }}
                                  />
                                ) : null
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Website Footer */}
                        {selectedClientWebsite && (
                          <>
                            <div className="border-t border-gray-300 mb-1"></div>
                            <div className="text-center text-gray-500 flex items-center justify-center gap-1" style={{ fontSize: '7px' }}>
                              <Globe className="w-2 h-2" />
                              {selectedClientWebsite}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {selectedLetter.versionCount > 0 && (
                <p className="mt-3 text-xs text-slate-500">
                  {selectedLetter.versionCount} version(s) saved
                </p>
              )}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Eye className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-200 mb-2">Select a letter</h3>
              <p className="text-slate-400">Click on a letter to preview its content</p>
            </div>
          )}
        </div>
      </div>

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
            <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between">
              <h3 className="font-medium text-slate-200">Generate Cold Letters</h3>
              <button
                onClick={() => setShowGenerateModal(false)}
                className="text-slate-400 hover:text-slate-200"
                disabled={generating}
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            {generating && generationProgress ? (
              <div className="p-8 flex flex-col items-center justify-center">
                <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
                <p className="text-slate-200 text-lg font-medium">Generating Letters</p>

                {/* Current action message */}
                <p className="text-blue-400 text-sm mt-3 font-medium">
                  {generationProgress.message}
                </p>

                {/* Progress bar */}
                <div className="mt-4 w-full max-w-sm">
                  <div className="flex justify-between text-xs text-slate-400 mb-1">
                    <span>{generationProgress.current} of {generationProgress.total}</span>
                    <span>{Math.round((generationProgress.current / generationProgress.total) * 100)}%</span>
                  </div>
                  <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all duration-300"
                      style={{ width: `${(generationProgress.current / generationProgress.total) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Stats */}
                <div className="mt-4 flex gap-4 text-xs">
                  {generationProgress.successCount > 0 && (
                    <span className="text-emerald-400">
                      ✓ {generationProgress.successCount} completed
                    </span>
                  )}
                  {generationProgress.skippedCount > 0 && (
                    <span className="text-amber-400">
                      ⏭ {generationProgress.skippedCount} skipped
                    </span>
                  )}
                  {generationProgress.failedCount > 0 && (
                    <span className="text-red-400">
                      ✗ {generationProgress.failedCount} failed
                    </span>
                  )}
                </div>

                <p className="text-slate-500 text-xs mt-6">Please don't close this window.</p>
              </div>
            ) : (
              <div className="p-4 space-y-4 overflow-auto">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Client
                  </label>
                  <select
                    value={selectedClientId || ''}
                    onChange={(e) => {
                      const id = Number(e.target.value)
                      setSelectedClientId(id)
                      setSelectedContactIds([])
                      fetchContacts(id)
                    }}
                    className="input w-full"
                  >
                    <option value="">Select a client...</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.companyName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-slate-300">
                      Cold Businesses ({contacts.length} available)
                    </label>
                    {contacts.length > 0 && (
                      <button
                        onClick={selectAllContacts}
                        className="text-xs text-blue-400 hover:text-blue-300"
                      >
                        {selectedContactIds.length === contacts.length ? 'Deselect All' : 'Select All'}
                      </button>
                    )}
                  </div>

                  {!selectedClientId ? (
                    <p className="text-slate-500 text-sm py-4 text-center">Select a client first</p>
                  ) : contacts.length === 0 ? (
                    <p className="text-slate-500 text-sm py-4 text-center">No cold businesses available for this client</p>
                  ) : (
                    <div className="border border-slate-700 rounded-lg max-h-64 overflow-y-auto">
                      {contacts.map((contact) => (
                        <div
                          key={contact.id}
                          onClick={() => toggleContactSelection(contact.id)}
                          className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-700/50 border-b border-slate-700/50 last:border-b-0 ${
                            selectedContactIds.includes(contact.id) ? 'bg-blue-500/10' : ''
                          }`}
                        >
                          {selectedContactIds.includes(contact.id) ? (
                            <CheckSquare className="w-5 h-5 text-blue-400 flex-shrink-0" />
                          ) : (
                            <Square className="w-5 h-5 text-slate-500 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-200 truncate">{contact.businessName}</p>
                            <p className="text-xs text-slate-500">
                              {[contact.town, contact.postcode].filter(Boolean).join(', ')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedContactIds.length > 0 && (
                    <p className="text-sm text-blue-400 mt-2">
                      {selectedContactIds.length} business(es) selected
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-700/50">
                  <button
                    onClick={() => {
                      setShowGenerateModal(false)
                      setSelectedContactIds([])
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGenerate}
                    disabled={selectedContactIds.length === 0 || !selectedClientId}
                    className="btn-primary flex items-center gap-2"
                  >
                    <Sparkles className="w-4 h-4" />
                    Generate {selectedContactIds.length > 0 ? `${selectedContactIds.length} Letter(s)` : 'Letters'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Letter Editor Modal */}
      {editingLetter && (
        <ColdLetterEditor
          letter={editingLetter}
          onUpdate={() => {
            fetchLetters()
            fetchStats()
            if (selectedLetter?.id === editingLetter.id) {
              api.get(`/api/v1/cold-letters/${editingLetter.id}`).then(res => {
                setSelectedLetter(res.data)
              })
            }
          }}
          onClose={() => setEditingLetter(null)}
        />
      )}
    </div>
  )
}
